import { Card, CardContent, toast } from '@estac1/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api } from '../../lib/api.js';
import { FlagRow } from './billing-form-fields.js';
import type { BillingFlags } from './types.js';

/**
 * Admin toggles for the billing features (#228). Everything the app enables
 * has an on/off switch here; features needing credentials we don't have yet
 * (Pix Automático) start off and stay off until turned on explicitly.
 */
export const BillingFlagsCard = () => {
  const qc = useQueryClient();
  const flags = useQuery({
    queryKey: ['admin-settings-flags'],
    queryFn: () => api.request<BillingFlags>('/v1/admin/settings/flags'),
  });

  const update = useMutation({
    mutationFn: (patch: Partial<BillingFlags> & Record<string, unknown>) =>
      api.request<BillingFlags>('/v1/admin/settings/flags', { method: 'PUT', json: patch }),
    onSuccess: () => {
      toast.success('Recursos de cobrança atualizados.');
      qc.invalidateQueries({ queryKey: ['admin-settings-flags'] });
      qc.invalidateQueries({ queryKey: ['admin-integrations-health'] });
    },
    onError: (err) => toast.error(err instanceof ApiError ? err.message : 'Falha ao salvar.'),
  });

  const f = flags.data;

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div>
          <h2 className="text-sm font-semibold">Recursos de cobrança</h2>
          <p className="text-xs text-muted-fg">
            Ligue ou desligue cada recurso. O que depende de credenciais que ainda não temos (Pix
            Automático) permanece desligado.
          </p>
        </div>
        {flags.isLoading || !f ? (
          <p className="text-sm text-muted-fg">Carregando...</p>
        ) : (
          <div className="space-y-3">
            <FlagRow
              label="Plano anual"
              hint={`Cobra ${f.billingOptions.annualMonthsCharged} meses (${12 - f.billingOptions.annualMonthsCharged} grátis)`}
              enabled={f.annualPlanEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ annualPlanEnabled: v })}
            />
            <FlagRow
              label="Desconto por pagamento automático"
              hint={`-${f.billingOptions.autopayDiscountPercent}% no cartão recorrente / Pix Automático`}
              enabled={f.autopayDiscountEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ autopayDiscountEnabled: v })}
            />
            <FlagRow
              label="Pix Automático"
              hint="Requer credenciais do provedor — mantenha desligado até tê-las"
              enabled={f.pixAutomaticoEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ pixAutomaticoEnabled: v })}
            />
            <FlagRow
              label="Alerta de upsell por capacidade"
              hint={`Sinaliza lotes que bateram o limite de vagas em ${f.capacityUpsell.minDaysAtCap} dias nos últimos ${f.capacityUpsell.windowDays}`}
              enabled={f.capacityUpsellEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ capacityUpsellEnabled: v })}
            />
            <FlagRow
              label="Recebimentos do estacionamento"
              hint="Subconta Asaas por estacionamento (valet e avulso). Mantenha desligado até validar o fluxo no sandbox"
              enabled={f.paymentsEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ paymentsEnabled: v })}
            />
            <FlagRow
              label="Nota fiscal (NFS-e)"
              hint="Mostra o módulo de nota fiscal aos donos. Só aparece com os Recebimentos ligados"
              enabled={f.nfseEnabled ?? false}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ nfseEnabled: v })}
            />
            <FlagRow
              label="WhatsApp"
              hint="Oferece o WhatsApp como canal dos alertas no módulo WhatsApp e SMS. Só funciona com os segredos da Meta no Worker e os modelos aprovados (docs/whatsapp.md)"
              enabled={f.whatsappEnabled ?? false}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ whatsappEnabled: v })}
            />
            <FlagRow
              label="Verificação diária das integrações"
              hint="Testa as chaves (Asaas, SMS, captcha, placas) uma vez por dia, sem enviar nada, e avisa por e-mail se algo parar de funcionar"
              enabled={f.credentialCanaryEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ credentialCanaryEnabled: v })}
            />
            <FlagRow
              label="Exclusão de contas canceladas"
              hint="Apaga tickets, fotos, mensalistas e operadores 30 dias depois do cancelamento, como dizem os Termos de Uso. Faturas e cadastro ficam"
              enabled={f.accountPurgeEnabled}
              pending={update.isPending}
              onToggle={(v) => update.mutate({ accountPurgeEnabled: v })}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};
